
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { sessionManager } from '@/services/auth/sessionManager';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/hooks/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Clock } from 'lucide-react';

export const SessionExpirationWarning = () => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleWarning = (data: { timeRemaining: number }) => {
      setTimeRemaining(data.timeRemaining);
      setShowWarning(true);
    };

    const handleExpired = () => {
      setShowWarning(false);
      toast({
        title: 'Session Expired',
        description: 'Your session has expired. Please log in again.',
        variant: 'destructive',
      });
      logout();
      navigate('/login');
    };

    const handleInactivity = () => {
      setShowWarning(false);
      toast({
        title: 'Session Expired',
        description: 'Your session expired due to inactivity. Please log in again.',
        variant: 'destructive',
      });
      logout();
      navigate('/login');
    };

    sessionManager.addEventListener('warning', handleWarning);
    sessionManager.addEventListener('expired', handleExpired);
    sessionManager.addEventListener('inactivity', handleInactivity);

    return () => {
      sessionManager.removeEventListener('warning', handleWarning);
      sessionManager.removeEventListener('expired', handleExpired);
      sessionManager.removeEventListener('inactivity', handleInactivity);
    };
  }, [logout, navigate, toast]);

  const handleDismiss = () => {
    setShowWarning(false);
  };

  const formatTime = (ms: number): string => {
    const minutes = Math.floor(ms / 60000);
    const seconds = Math.floor((ms % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <AlertDialog open={showWarning} onOpenChange={setShowWarning}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <div className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-warning" />
            <AlertDialogTitle>Session Expiring Soon</AlertDialogTitle>
          </div>
          <AlertDialogDescription>
            Your session will expire in approximately {formatTime(timeRemaining)}. 
            Your work will be saved, but you'll need to log in again to continue.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogAction onClick={handleDismiss}>
            I Understand
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
